import { ComponentType, memo } from 'react';
import { extractErrorMessage } from 'app/providers/with-wagmi-auth/utils';
import { useTap2MineReferralCode } from 'features/tap2mine-referral';
import { getToastMessage } from 'shared';
import { AwaitableButton, AwaitableButtonProps } from 'shared/ui/buttons';

interface ConfirmPaymentButtonProps extends AwaitableButtonProps {
  onSuccessConfirm?: () => void;
}

export const ConfirmPaymentButton = memo(
  ({ children, onSuccessConfirm, disabled, action, isLoading, ...props }: ConfirmPaymentButtonProps) => {
    const handleConfirm = async () => {
      try {
        await action?.();
        onSuccessConfirm?.();
      } catch (error) {
        getToastMessage('error', extractErrorMessage(error));
      }
    };

    return (
      <AwaitableButton
        {...props}
        isLoading={isLoading}
        disabled={disabled}
        size="medium"
        fullWidth
        action={handleConfirm}
      >
        {children ?? 'Confirm'}
      </AwaitableButton>
    );
  },
);

interface WithReferralCodeProps extends Omit<ConfirmPaymentButtonProps, 'action'> {
  confirmAction: (code: string) => Promise<void>;
}

export const withReferralCode = (WrappedComponent: ComponentType<AwaitableButtonProps>) => {
  return memo((props: WithReferralCodeProps) => {
    const { confirmAction, ...restProps } = props;
    const { code, isValidCode, isValidating } = useTap2MineReferralCode();

    return (
      <WrappedComponent
        {...restProps}
        isLoading={isValidating}
        disabled={!isValidCode || restProps.disabled}
        action={() => confirmAction(code)}
      />
    );
  });
};

export const ConfirmPaymentButtonWithReferralCode = withReferralCode(ConfirmPaymentButton);
